import { type ReactNode, useState } from 'react';
import { Modal } from 'antd';

import { type KboixMidsole } from 'types/product';
import { useI18n } from 'context/i18n';
import { interpolate } from 'utils/StringUtils';

import styles from './style.module.css';

type MidsoleCardProps = {
  sole: KboixMidsole;
  corner: ReactNode;
  selected: boolean;
  disabled?: boolean;
  hideImage?: boolean;
};

export function MidsoleCard({ sole, corner, selected, disabled, hideImage }: MidsoleCardProps) {
  return (
    <div
      className="group relative flex w-full border border-[#bebebe] text-left data-[selected=false]:bg-[#d9d9d9] data-[selected=false]:text-[var(--color-black)]"
      data-selected={selected}
    >
      <div data-hide={hideImage} className="size-[7.5rem] flex-shrink-0 flex-grow-0 md:data-[hide=true]:hidden">
        <img src={sole.images} alt="" className="h-full w-full object-cover" />
      </div>
      <div className="flex min-w-[184px] flex-grow flex-col p-2.5 text-[0.6875rem] font-light">
        <div className="flex items-start justify-between gap-2.5 font-secondary font-bold">
          <div>{sole.name}</div>
          <div>{corner}</div>
        </div>
        <div className="overflow-hidden">{sole.description}</div>
      </div>
      {disabled ?
        <div className="absolute inset-0 z-10 bg-[var(--color-black)]"></div>
      : null}
    </div>
  );
}

type KboixMidsolesProps = {
  midsoles?: KboixMidsole[];
  selectedSize: string;
  selected: KboixMidsole[];
  setSelected: (selected: KboixMidsole[]) => void;
  canConfig?: boolean;
  min?: number;
};
export function KboixMidsoles({ midsoles, selectedSize, selected, setSelected, canConfig = true, min = 2 }: KboixMidsolesProps) {
  const { t } = useI18n();
  // const to = useLink();
  const [open, setOpen] = useState(false);

  const handleSelect = (midsole: KboixMidsole) => {
    if (selected.some((s) => s.id === midsole.id)) {
      setSelected(selected.filter((s) => s.id !== midsole.id));
    } else {
      setSelected([...selected, midsole]);
    }
  };

  const selectedIds = selected.map((s) => s.id);

  const removeSelected = (midsoleId: string) => {
    setSelected(selected.filter((s) => s.id !== midsoleId));
  };

  return (
    <div className="mt-7 flex flex-col items-center justify-center bg-[#f3f4f6] px-5 py-6 text-center text-[var(--color-black)] max-md:-mx-8">
      <ul className="mt-3.5 flex w-full list-none flex-col gap-2.5 p-0 empty:hidden">
        <li className="text-[0.6875rem] font-light leading-5 last:hidden">
          {interpolate(t('kboix.soles', 'two.included'), {
            '{{min}}': min.toString(),
          })}
        </li>
        {[...selected].slice(0, min).map((s) => (
          <li key={s.id}>
            <MidsoleCard
              sole={s}
              hideImage={true}
              selected={selectedIds.includes(s.id)}
              corner={
                canConfig ?
                  <button
                    className="jusc mt-0 flex items-center border-none bg-transparent p-0 text-[0.6875rem] font-light focus-visible:outline-[var(--color-white)]"
                    onClick={() => {
                      removeSelected(s.id);
                    }}
                  >
                    <span className="sr-only">{t('kboix.soles', 'remove.midsole')}</span>
                    <svg aria-hidden xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                      <path
                        d="M0 7.5C0 3.33333 3.33333 0 7.5 0C11.6667 0 15 3.33333 15 7.5C15 11.6667 11.6667 15 7.5 15C3.33333 15 0 11.6667 0 7.5ZM5 11.7708L7.5 9.27083L10 11.7708L11.7708 10L9.27083 7.5L11.7708 5L10 3.22917L7.5 5.72917L5 3.22917L3.22917 5L5.72917 7.5L3.22917 10L5 11.7708Z"
                        fill="black"
                      />
                    </svg>
                  </button>
                : null
              }
            />
          </li>
        ))}
        <li className="text-[0.6875rem] font-light leading-5 last:hidden">{t('kboix.soles', 'extra.midsoles')}</li>
        {[...selected].slice(min).map((s) => (
          <li key={s.id}>
            <MidsoleCard
              sole={s}
              hideImage={true}
              selected={selectedIds.includes(s.id)}
              corner={
                canConfig ?
                  <button
                    className="mt-0 flex items-center border-none bg-transparent p-0 text-[0.6875rem] font-light focus-visible:outline-[var(--color-white)]"
                    onClick={() => {
                      removeSelected(s.id);
                    }}
                  >
                    <span className="sr-only">{t('kboix.soles', 'remove.midsole')}</span>
                    <svg aria-hidden xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                      <path
                        d="M0 7.5C0 3.33333 3.33333 0 7.5 0C11.6667 0 15 3.33333 15 7.5C15 11.6667 11.6667 15 7.5 15C3.33333 15 0 11.6667 0 7.5ZM5 11.7708L7.5 9.27083L10 11.7708L11.7708 10L9.27083 7.5L11.7708 5L10 3.22917L7.5 5.72917L5 3.22917L3.22917 5L5.72917 7.5L3.22917 10L5 11.7708Z"
                        fill="black"
                      />
                    </svg>
                  </button>
                : null
              }
            />
          </li>
        ))}
      </ul>
      {canConfig ?
        <button className="flex flex-col items-center justify-center border-none bg-transparent font-light" onClick={() => setOpen(true)}>
          <svg aria-hidden width="32" height="48" viewBox="0 0 32 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="16" cy="21" r="14.5" stroke="black" />
            <path d="M16.544 21.232H23.2V22.32H16.544V28.976H15.456V22.32H8.8V21.232H15.456V14.576H16.544V21.232Z" fill="black" />
          </svg>
          <span className="[&_span]:font-secondary [&_span]:font-bold" dangerouslySetInnerHTML={{ __html: t('kboix.soles', 'add.or.remove.midsoles') }} />
        </button>
      : null}
      {canConfig ?
        /* @ts-ignore ANTD 😩 */
        <Modal width={undefined} visible={open} onCancel={() => setOpen(false)} className={styles.modalContainer} closable={false} footer={null} centered>
          <div className="flex items-center justify-center">
            <button
              className="flex size-7 items-center justify-center rounded-full border border-[var(--color-white)] bg-transparent outline-[var(--color-white)] transition-all duration-150 ease-in-out hover:bg-[var(--color-white)] hover:text-[var(--color-black)]"
              onClick={() => setOpen(false)}
              disabled={!midsoles}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path
                  d="M6.94902 6.18234L11.6555 10.8888L10.8862 11.6582L6.17969 6.95167L1.47318 11.6582L0.703852 10.8888L5.41036 6.18234L0.703852 1.47584L1.47318 0.706503L6.17969 5.41301L10.8862 0.706503L11.6555 1.47584L6.94902 6.18234Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </div>
          <div className="my-5 flex items-center justify-between text-xl font-light">
            {t('kboix.soles', 'select.midsoles')} <span>{selected.length}</span>
          </div>
          <div className="flex h-full flex-grow flex-col overflow-hidden">
            <ul className="flex h-full w-full flex-col gap-2.5 overflow-y-auto p-0.5">
              {midsoles?.map((midsole) => {
                const disabled = midsole.sizes.every((s) => !s.available) || midsole.sizes.find((s) => s.value === selectedSize)?.available === false;
                return (
                  <li key={midsole.id}>
                    <button
                      className="flex w-full border-none bg-transparent p-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--color-white)]"
                      onClick={() => handleSelect(midsole)}
                      disabled={disabled}
                    >
                      <MidsoleCard
                        sole={midsole}
                        corner={
                          disabled ? null : (
                            <div className="mt-0 flex items-center border-none bg-transparent p-0 text-[0.6875rem] font-light focus-visible:outline-[var(--color-white)]">
                              {selected.some((s) => s.id === midsole.id) ?
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="15" viewBox="0 0 16 15" fill="none">
                                  <path
                                    d="M0 7.5C0 3.33333 3.3372 0 7.50869 0C11.6802 0 15.0174 3.33333 15.0174 7.5C15.0174 11.6667 11.6802 15 7.50869 15C3.3372 15 0 11.6667 0 7.5ZM2.60718 7.70833L6.46582 11.5625L12.2016 5.83333L10.4287 4.0625L6.46582 8.02083L4.38007 5.9375L2.60718 7.70833Z"
                                    fill="white"
                                  />
                                </svg>
                              : <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                  <path
                                    d="M3.1967 3.19686C6.14298 0.250577 10.857 0.250577 13.8033 3.19686C16.7496 6.14313 16.7496 10.8572 13.8033 13.8035C10.857 16.7497 6.14298 16.7497 3.1967 13.8035C0.250421 10.8572 0.250421 6.14313 3.1967 3.19686ZM3.7123 9.75232H7.24783V13.2879H9.75217V9.75232H13.2877L13.2877 7.24799H9.75217V3.71245H7.24783V7.24799H3.7123L3.7123 9.75232Z"
                                    fill="black"
                                  />
                                </svg>
                              }
                            </div>
                          )
                        }
                        selected={selected.some((s) => s.id === midsole.id)}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="flex items-center justify-center">
            <button
              className="flex h-10 items-center justify-center border border-[var(--color-white)] bg-[var(--color-white)] px-3.5 text-[var(--color-black)] outline-[var(--color-white)] transition-all duration-150 ease-in-out hover:bg-transparent hover:text-[var(--color-white)]"
              onClick={() => setOpen(false)}
            >
              {t('kboix.soles', 'back.to.product')}
            </button>
          </div>
        </Modal>
      : null}
    </div>
  );
}
